import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import BPRSForm from "./BPRSForm";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
    fetchBprsAssessment,
    updateBprsAssessment,
} from "../../../store/slices/bprsAssessmentsSlice";

const EditBPRS = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const { currentBprsAssessment, isLoading } = useSelector(
        (state) => state.bprsAssessments
    );

    const [formData, setFormData] = useState({});

    // ===========================================
    // 1) FETCH DATA ON LOAD
    // ===========================================
    useEffect(() => {
        dispatch(fetchBprsAssessment(id));
    }, [dispatch, id]);

    // ===========================================
    // 2) MAP BACKEND → FORM DATA
    // ===========================================
    useEffect(() => {
        if (!currentBprsAssessment) return;

        // Map backend fields to camelCase (same as Add component)
        const mapped = {
            caseId: currentBprsAssessment.case_id || "",
            patientId: currentBprsAssessment.patient_id || "",
            clientName: currentBprsAssessment.client_name || "",
            clientId: currentBprsAssessment.client_id || "",
            date: currentBprsAssessment.date || "",
            md: currentBprsAssessment.md || "",
            clientName: currentBprsAssessment.client_name || "",
            diagnosis: currentBprsAssessment.diagnosis || "",
            assessedBy: currentBprsAssessment.assessed_by || "",
        };

        // Map all 18 BPRS scoring items (score + value)
        const items = [
            "somaticConcern",
            "anxiety",
            "emotionalWithdrawal",
            "conceptualDisorganization",
            "guiltFeelings",
            "tension",
            "mannerismsPosturing",
            "grandiosity",
            "depressiveMood",
            "hostility",
            "suspiciousness",
            "hallucinatoryBehavior",
            "motorRetardation",
            "uncooperativeness",
            "unusualThoughtContent",
            "bluntedAffect",
            "excitement",
            "disorientation",
        ];

        items.forEach((key) => {
            const snake = key.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);

            mapped[key] = String(currentBprsAssessment[`${snake}_score`] ?? "");
            mapped[key + "Label"] = currentBprsAssessment[`${snake}_value`] ?? "";
        });

        setFormData(mapped);
    }, [currentBprsAssessment]);

    // ===========================================
    // 3) HANDLE INPUT CHANGE
    // ===========================================
    const handleChange = (e) => {
        const name = e.target.name;
        const value = e.target.value;
        const fullLabel = e.target.getAttribute("data-label") || "";

        // Extract just the label part (remove the "X = " prefix)
        let label = fullLabel;
        if (fullLabel.includes("=")) {
            label = fullLabel.split("=")[1]?.trim() || "";
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value,
            [`${name}Label`]: label,
        }));
    };

    // ===========================================
    // 4) SUBMIT FORM (UPDATE)
    // ===========================================
    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {};

        // Convert camelCase → snake_case_score/value
        Object.keys(formData).forEach((key) => {
            if (!formData[key]) return;

            if (/Label$/.test(key)) return;

            const snake = key.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);

            payload[`${snake}_score`] = formData[key];
            payload[`${snake}_value`] = formData[`${key}Label`] || "";
        });

        // Add top-level fields
        payload.patient_id = formData.patientId;
        payload.case_id = formData.caseId;
        payload.client_id = formData.clientId;
        payload.client_name = formData.clientName;
        payload.diagnosis = formData.diagnosis;
        payload.assessed_by = formData.assessedBy;
        payload.date = formData.date;
        payload.md = formData.md;

        // Dispatch update thunk
        const result = await dispatch(
            updateBprsAssessment({ case_id: caseId, assessmentData: payload })
        );

        if (result.meta.requestStatus === "fulfilled") {
            Swal.fire("Updated!", "BPRS assessment updated successfully.", "success");
            navigate(`/cases/${caseId}/profile`, { state: { patientId, caseId } });
        } else {
            Swal.fire("Error", "Failed to update assessment", "error");
        }
    };

    // if (isLoading || !formData.date) {
    //     return (
    //         <MainLayout>
    //             <div className="container-xxl py-4">
    //                 <h4>Loading...</h4>
    //             </div>
    //         </MainLayout>
    //     );
    // }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase04/bprs/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>

                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <BPRSForm formData={formData} handleChange={handleChange} />

                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button type="submit" className="btn btn-primary px-4">
                                        <i className="ri-edit-2-line me-1"></i>Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditBPRS;
